import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { Company, CompanyDetail, PaginatedResponse } from "@/lib/types"

interface CompanyFilters {
  search?: string
  status_id?: string
  industry_id?: string
  acquisition_source_id?: string
  owner_id?: string
  city?: string
  created_at_from?: string
  created_at_to?: string
  sort?: string
  cursor?: string
  limit?: number
}

function buildParams(filters: CompanyFilters): string {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function useCompanies(filters: CompanyFilters = {}) {
  return useQuery({
    queryKey: ["companies", filters],
    queryFn: () => {
      const qs = buildParams(filters)
      return api.get<PaginatedResponse<Company>>(`/api/v1/companies${qs ? `?${qs}` : ""}`)
    },
  })
}

export function useCompany(id: string | undefined) {
  return useQuery({
    queryKey: ["companies", id],
    queryFn: () => api.get<{ data: CompanyDetail }>(`/api/v1/companies/${id}`),
    enabled: !!id,
  })
}

export function useCreateCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: Company }>("/api/v1/companies", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] })
    },
  })
}

export function useUpdateCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: Company }>(`/api/v1/companies/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] })
    },
  })
}

export function useNipLookup() {
  return useMutation({
    mutationFn: (nip: string) =>
      api.get<{
        data: {
          gus_data: {
            name?: string
            nip?: string
            regon?: string
            street?: string
            city?: string
            postal_code?: string
            state?: string
            status_nip?: string
            type?: string
            end_date?: string
          }
          existing_company: { id: string; name: string } | null
        }
      }>(`/api/v1/companies/lookup_nip?nip=${encodeURIComponent(nip)}`),
  })
}

export function useDeleteCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/companies/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] })
    },
  })
}
